/**
 * Account service for the admin account-management API.
 */

import apiClient from '../api/client';
import { User } from '../types/auth';
import {
    Account,
    AccountCreate,
    AccountUpdate,
    AccountListResponse,
    AccountSwitchResponse,
    AccountUserCreate,
    AccountUserListResponse,
} from '../types/account';

export const accountsService = {
    /** List all accounts (admin only). */
    async getAccounts(skip = 0, limit = 100): Promise<AccountListResponse> {
        const response = await apiClient.get<AccountListResponse>('/api/v1/accounts', {
            params: { skip, limit },
        });
        return response.data;
    },

    /** Get a single account. */
    async getAccount(accountId: string): Promise<Account> {
        const response = await apiClient.get<Account>(`/api/v1/accounts/${accountId}`);
        return response.data;
    },

    /** Get the caller's current (effective) account. */
    async getMyAccount(): Promise<Account> {
        const response = await apiClient.get<Account>('/api/v1/accounts/me');
        return response.data;
    },

    /** Create a new account. */
    async createAccount(data: AccountCreate): Promise<Account> {
        const response = await apiClient.post<Account>('/api/v1/accounts', data);
        return response.data;
    },

    /** Update an account. */
    async updateAccount(accountId: string, data: AccountUpdate): Promise<Account> {
        const response = await apiClient.patch<Account>(
            `/api/v1/accounts/${accountId}`,
            data
        );
        return response.data;
    },

    /** Lock an account (status flag only). */
    async lockAccount(accountId: string): Promise<Account> {
        const response = await apiClient.post<Account>(
            `/api/v1/accounts/${accountId}/lock`
        );
        return response.data;
    },

    /** Unlock an account. */
    async unlockAccount(accountId: string): Promise<Account> {
        const response = await apiClient.post<Account>(
            `/api/v1/accounts/${accountId}/unlock`
        );
        return response.data;
    },

    /**
     * Switch the admin's effective account (impersonation). Pass null to revert
     * to the admin's home account.
     */
    async switchAccount(accountId: string | null): Promise<AccountSwitchResponse> {
        const response = await apiClient.post<AccountSwitchResponse>(
            '/api/v1/accounts/switch',
            { account_id: accountId }
        );
        return response.data;
    },

    /** List users belonging to an account. */
    async getUsers(accountId: string, skip = 0, limit = 100): Promise<AccountUserListResponse> {
        const response = await apiClient.get<AccountUserListResponse>(
            `/api/v1/accounts/${accountId}/users`,
            { params: { skip, limit } }
        );
        return response.data;
    },

    /** Create a user under an account. */
    async createUser(accountId: string, data: AccountUserCreate): Promise<User> {
        const response = await apiClient.post<User>(
            `/api/v1/accounts/${accountId}/users`,
            data
        );
        return response.data;
    },
};

export default accountsService;
